/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Nahrazovač obecných datových zdrojů
 *
 * Stará se o kompletní nahrazení vybraných datových zdrojů dle nového obsahu.
 */
class ReplacerCommon {
  /**
   * Vytvoří nahrazovač datových zdrojů.
   * @param {Store} primaryStore Hlavní datový sklad ve kterém je uložen přehrávaný obsah.
   */
  constructor(primaryStore) {
    this.primaryStore = primaryStore;
    // Názvy datových zdrojů ke kompletní výměně.
    this.resources = [
      { contentInfoKey: 'layerArray', resourceName: 'layer' },
      { contentInfoKey: 'layoutArray', resourceName: 'layout' },
      { contentInfoKey: 'mediumArray', resourceName: 'medium' },
      { contentInfoKey: 'playlistArray', resourceName: 'playlist' },
      { contentInfoKey: 'taskArray', resourceName: 'task' },
      { contentInfoKey: 'timerArray', resourceName: 'timer' },
      { contentInfoKey: 'trackArray', resourceName: 'track' }
    ];
  }

  /**
   * Nahradí stávající obsah datových zdrojů za nový.
   * @param {ContentInfo} contentInfo
   * @returns {Promise.<ReplacerCommonResult>}
   */
  replace(contentInfo) {
    // TODO #95 Co když selže?
    // - Přerušit obnovování v sérii po jednotlivých datových skladech? Ano.
    // - Co s těmi co už uspěli? Pokusit se vrátit zpět.
    // - Co s nekonzistentním stavem, který to vyvolá? Předejít mu. Buď beze změny, nebo prázdný obsah.
    // - Ale to je obecně problém v celém replace algoritmu. Prověřit všude.
    // - Pokusit se smazat obsah, jako zotavení? A nový pokus? Nový pokus by měl asi řídit heartbeat. Mělo by být stejné pro všechny kroky Replaceru.
    // - Datový sklad v paměti musí být vždy konzistentní!
    // - Používat inject až po všech dokončených findAll.
    // - Celé toto bude pravděpodobně nutné řídit z vyšší úrovně (např. i v session).
    //   - Předávat attrsArray, attrsMap?
    //   - [{idArray: ['abc', ...], resourceName: 'medium'}, ...]
    //   - Pokud se v budoucnu změní rozhodnutí co předávat, lze upravit třeba na {recordArray, resourceName}.
    // TODO #95 No a to celé bude nutno sloučit se ReplacerSession, jeslikož jsou data propojená.
    // TODO #95 Obecně musí být celá operace nahrazení obsahu atomická a dovolit jen 3 stavy:
    //   - Úspěch => Obsah byl nahrazen.
    //   - Neúspěch => Obsah zůstal původní.
    //   - Kritický neúspěch => Prázdný obsah, ale ne namixovaný, nebo nezotavitelný.
    // TODO #95 Algoritmus s jedním datovým zdrojem:
    // - První async krok by měl nastavit prázdný obsah (session), aby se při neúspěchu nezobrazil nějaký mix.
    // - Sériově(!) pro každý datový zdroj (session poslední):
    //   - Vyfiltrovat obsah z contentStore.
    //   - Provést replaceAsync na primaryStore bez ovlivnění aktuální paměti (obsah co se přehrává).
    //   - http://www.js-data.io/v2.9/docs/dsdefaults#section-cacheresponse
    // - Sériově(!) pro každý datový zdroj (session poslední):
    //   - Provést replace (sync) na primaryStore pro všechny nyní komplentě načtené.
    // - Pokud něco selže (jen u async?):
    //   - Pokusit se vrátit původní obsah u všech změněných datových zdrojů. Sériově. Session poslední. Pak throw.
    //   - Pokud i to selže, tak smazat všechna data? Co session?
    //     - Ta už by teoreticky měla být smazaná z prvního kroku.
    //     - Možná určit co smazat ze session podle toho, co by přepsal content.
    //     - Anebo tyto informace vyčlenit do nového datového zdroje a ten bez řešení podrobností replacovat?
    // TODO #95 Algoritmus se dvěma datovými zdroji:
    // - Jeden datový zdroj s přehrávaným obsahem a druhý s novým. Střídají se.
    // - Tím, že se datové zdroje vymění, tak není třeba řešit nekonzistenci náhradou v rámci jednoho.
    // - Při každé změně obsahu se předává i odkaz na kompletně nový datový sklad.
    // - Starý se pak na pozadí vyčistí a připraví pro nový obsah.
    // - Co s hodnotami v sessions, které nejsou součástí obsahu?
    return new Promise((resolve) =>
      resolve(
        // Sériové volání nahrazení pro všechny datové zdroje.
        this.resources.reduce(
          (acc, { contentInfoKey, resourceName }) =>
            acc.then((results) => {
              const models = contentInfo[contentInfoKey];
              const attrsArray = JSON.parse(JSON.stringify(models));
              return this.primaryStore
                .replaceAsync(resourceName, attrsArray, null, {
                  onConflict: 'replace'
                })
                .then((replacedModels) => {
                  results[resourceName] = replacedModels;
                  return results;
                });
            }),
          Promise.resolve({})
        )
      )
    );
  }

  /**
   * Obnoví obsah datových zdrojů v datovém skladě.
   * @returns {Promise.<ReplacerCommonResult>}
   */
  restore() {
    return new Promise((resolve) =>
      resolve(
        // Sériové volání obnovení pro všechny datové zdroje.
        this.resources.reduce(
          (acc, { resourceName }) =>
            acc.then((results) =>
              this.primaryStore.findAll(resourceName).then((models) => {
                results[resourceName] = models;
                return results;
              })
            ),
          Promise.resolve({})
        )
      )
    );
  }
}

export default ReplacerCommon;
